import { Id } from '@/infra/id'
import { Model, type ModelProps } from '@/infra/model'
import { observable, action, computed } from 'mobx'
import { collection } from '@/infra/collection'
import { unixEpoch } from '@/utils/date-util'
import { Participant, type ParticipantProps } from './Participant'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { Message } from './Message'
import { filter, find, takeUntil } from 'rxjs'
import { share } from 'rxjs'
import { messageStatusDeltaStream } from './message-status-delta-stream'
import { toStream } from '@/utils/mobx-util'
import type { User } from '@/features/iam/users/User'
import { randomUUID } from '@/utils/uuid-util'

export interface ConversationProps extends Omit<
  ModelProps<Conversation>,
  'participants'
> {
  participants?: ParticipantProps[]
  workspace: Workspace
}

/**
 * Represents a messaging conversation between participants.
 */
export class Conversation extends Model<Conversation> {
  id = Id.none

  @observable private accessor _unreadCount = 0

  @observable accessor workspace: Workspace = Workspace.none
  @observable accessor externalId: Id | null = null
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()

  @observable accessor meta: ConversationMeta = {
    type: 'Unknown',
  }

  /**
   * Collection of participants in this conversation.
   */
  readonly participants = collection({
    getDataId: (d) => d.userId,
    getModelId: (m) => m.userId,
    create: () => new Participant(),
  })

  /**
   * Collection of messages in this conversation.
   */
  readonly messages = collection({
    getDataId: (d) => d.cid,
    getModelId: (m) => m.cid,
    create: () => this._createMessage(),
  })

  /**
   * The number of unread messages in this conversation.
   */
  @computed get unreadCount() {
    return this._unreadCount
  }

  /** @inheritDoc */
  @action.bound
  override set({ participants, ...props }: Partial<ConversationProps>): this {
    if (participants) {
      this.participants.reset(participants)
    }

    return super.set(props)
  }

  /**
   * Sets the unread count to the specified value.
   */
  @action.bound
  setUnreadCount(count: number) {
    this._unreadCount = count
  }

  /**
   * Prepares a new message to be sent by optimistically adding it to
   * the collection. This message can be passed to the store to send it.
   *
   * This special-casing is done so we can show the message in the UI
   * as quickly as possible and offer an inline retry option.
   */
  @action.bound
  prepareNewMessage({ sender, text }: PrepareNewMessageOptions): Message {
    const now = new Date()
    return this.messages.set({
      cid: randomUUID(),
      sender: sender,
      dateCreated: now,
      dateModified: now,
      conversationId: this.id,
      sendOperationState: 'Sending',
      content: {
        type: 'Text',
        text,
      },
    })
  }

  /**
   * Increases or decreases the unread count by the specified amount.
   *
   * @param delta
   */
  @action.bound
  varyUnreadCount(delta: number) {
    this._unreadCount = Math.max(this._unreadCount + delta, 0)
  }

  /**
   * Creates a new message model instance and subscribes to its status changes
   * to update the unread count in real-time.
   */
  private _createMessage(): Message {
    const message = new Message()
    message.conversationId = this.id

    // An observable that emits a value and completes when the
    // message is removed from the collection.
    const messageRemoved$ = this.messages.removals$.pipe(
      find((m) => m === message),
      share(),
    )

    // Status changes pertaining to the current user.
    const myStatusChanges$ = message.statusChanges$.pipe(
      filter((status) => status.user.isCurrentUser),
    )

    // An observable that emits a value and completes
    // when the message is read by the current user.
    const readByCurrentUser$ = toStream(
      () => message.currentUserStatus?.deliveryStatus,
      true,
    ).pipe(find((status) => status === 'Read'))

    // Stream of deltas used to vary the unread count when the message receipt
    // status is changed for the current user. It'll clean itself up when the
    // message is read or removed.
    messageStatusDeltaStream(myStatusChanges$, messageRemoved$)
      .pipe(
        // At this point, the message props haven't been set to anything yet,
        // so it's possible that we're hydrating a message already read by
        // the current user. In that case, we won't be getting any status changes
        // for it, so we'll complete the subscription once we detect that the message
        // is read by the current user.
        takeUntil(readByCurrentUser$),
      )
      // Update the unread count based on the delta.
      .subscribe(this.varyUnreadCount)

    return message
  }
}

/**
 * Represents the meta data for a conversation.
 */
export type ConversationMeta = UnknownMeta | JobMeta

/**
 * Represents unknown meta data for a conversation.
 */
export interface UnknownMeta {
  type: 'Unknown'
}

/**
 * Represents job meta data for a conversation.
 */
export interface JobMeta {
  type: 'Job'
  id: Id
  name: string
}

export interface PrepareNewMessageOptions {
  /**
   * The sender of the message. Usually the current user.
   */
  sender: User
  /**
   * The text message.
   */
  text: string
}
